import { StyleSheet } from '@react-pdf/renderer'
import { PDF_COLORS } from 'features/reporting/pdfGeneration/constants/colors'
import { PDF_LAYOUT } from 'features/reporting/pdfGeneration/constants/layout'
import { PDF_TYPOGRAPHY } from 'features/reporting/pdfGeneration/constants/typography'

// Valid values for fontWeight in react-pdf
type ValidFontWeight =
  | 'normal'
  | 'bold'
  | 'medium'
  | 'thin'
  | 'hairline'
  | 'ultralight'
  | 'extralight'
  | 'light'
  | 'semibold'
  | 'demibold'
  | 'ultrabold'
  | 'extrabold'
  | 'heavy'
  | 'black'
  | number
  | undefined

// Valid values for textAlign in react-pdf
type ValidTextAlign = 'left' | 'center' | 'right' | 'justify' | undefined

// Valid values for flexDirection in react-pdf
type ValidFlexDirection =
  | 'row'
  | 'column'
  | 'row-reverse'
  | 'column-reverse'
  | undefined

// Valid values for alignItems in react-pdf
type ValidAlignItems =
  | 'center'
  | 'flex-start'
  | 'baseline'
  | 'flex-end'
  | 'stretch'

// Valid values for justifyContent in react-pdf
type ValidJustifyContent =
  | 'center'
  | 'flex-start'
  | 'flex-end'
  | 'space-between'
  | 'space-around'
  | 'space-evenly'
  | undefined

export const PDFStyles = StyleSheet.create({
  // ================ Page Layout ================
  page: {
    padding: PDF_LAYOUT.PADDING.PAGE,
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.MEDIUM,
    fontFamily: PDF_TYPOGRAPHY.FONT_FAMILY.PRIMARY,
  },

  content: {
    margin: PDF_LAYOUT.MARGIN.PAGE,
    marginBottom: 40,
    padding: 0,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },

  contentWithPadding: {
    margin: PDF_LAYOUT.MARGIN.PAGE,
    padding: PDF_LAYOUT.PADDING.CONTENT,
    flexGrow: 1,
  },
  // ================ Header Components ================
  header: {
    backgroundColor: PDF_COLORS.PRIMARY,
    height: 40,
    position: 'relative',
    flexDirection: 'row',
    gap: 16,
    alignItems: 'center',
    marginBottom: 18,
  },

  logoContainer: {
    width: 40,
    height: 40,
    backgroundColor: PDF_COLORS.PRIMARY,
    position: 'absolute',
    bottom: -12,
    left: '11%',
    transform: 'translateX(-50%)',
  },

  headerTitle: {
    textAlign: 'right',
    width: '100%',
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.XXXLARGE,
    lineHeight: PDF_TYPOGRAPHY.LINE_HEIGHT.TIGHT,
    fontWeight: 'bold',
    color: PDF_COLORS.TEXT_LIGHT,
    flexGrow: 1,
  },

  // ================ Subheader Components ================
  subheaderContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginBottom: PDF_LAYOUT.SPACING.MEDIUM,
  },

  decorationContainer: {
    flexDirection: 'column',
  },

  decorativeLongLine: {
    height: 2,
    margin: `${PDF_LAYOUT.SPACING.SMALL} ${PDF_LAYOUT.SPACING.MEDIUM} ${PDF_LAYOUT.SPACING.XSMALL} 0`,
    width: 20,
    backgroundColor: PDF_COLORS.TEXT_MUTED,
  },

  decorativeShortLine: {
    height: 2,
    margin: `${PDF_LAYOUT.SPACING.SMALL} ${PDF_LAYOUT.SPACING.XXLARGE} ${PDF_LAYOUT.SPACING.XSMALL} 0`,
    width: 8,
    backgroundColor: PDF_COLORS.TEXT_MUTED,
  },

  decorativeMediumLine: {
    height: 2,
    margin: `${PDF_LAYOUT.SPACING.SMALL} ${PDF_LAYOUT.SPACING.LARGE} ${PDF_LAYOUT.SPACING.XSMALL} 0`,
    width: 16,
    backgroundColor: PDF_COLORS.TEXT_MUTED,
  },

  subheaderTitle: {
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.MEDIUM,
    lineHeight: PDF_TYPOGRAPHY.LINE_HEIGHT.NORMAL,
    fontWeight: 'bold',
    color: PDF_COLORS.TEXT_PRIMARY,
  },

  // ================ Footer Components ================
  footerContainer: {
    position: 'absolute',
    bottom: PDF_LAYOUT.SPACING.XXXLARGE,
    left: 0,
    right: 0,
    marginHorizontal: PDF_LAYOUT.SPACING.XXXLARGE,
  },

  footerLine: {
    borderTop: `${PDF_LAYOUT.BORDER_WIDTH.NORMAL}px solid ${PDF_COLORS.TEXT_TERTIARY}`,
    marginBottom: PDF_LAYOUT.SPACING.LARGE,
  },

  footerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.XXSMALL,
    color: PDF_COLORS.TEXT_SECONDARY,
  },

  footerJobNumber: {
    width: '33%',
    textAlign: 'left' as ValidTextAlign,
    fontWeight: 'bold' as ValidFontWeight,
  },

  footerRevisionInfo: {
    width: '33%',
    textAlign: 'center' as ValidTextAlign,
    fontWeight: 'bold' as ValidFontWeight,
  },

  footerPageNumber: {
    width: '33%',
    textAlign: 'right' as ValidTextAlign,
  },

  // ================ Section Styles ================
  section: {
    marginBottom: 12,
    paddingBottom: 8,
    borderBottomWidth: PDF_LAYOUT.BORDER_WIDTH.NORMAL,
    borderBottomColor: PDF_COLORS.BORDER_MEDIUM,
  },

  sectionHeader: {
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.MEDIUM,
    marginBottom: 6,
    textDecoration: 'underline',
    fontWeight: 'bold',
    color: PDF_COLORS.ACCENT,
  },

  subsection: {
    marginTop: PDF_LAYOUT.SPACING.XXSMALL,
    marginBottom: PDF_LAYOUT.SPACING.XXSMALL,
  },

  subsectionTitle: {
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.SMALL,
    fontWeight: 'bold',
    marginBottom: PDF_LAYOUT.SPACING.XXSMALL,
    color: PDF_COLORS.TEXT_SECONDARY,
  },

  // ================ Cover Page Styles ================
  coverHeader: {
    backgroundColor: PDF_COLORS.PRIMARY,
    alignItems: 'center',
    padding: 16,
    paddingBottom: 20,
    marginBottom: 40,
  },

  coverHeaderContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },

  coverHeaderText: {
    color: PDF_COLORS.TEXT_LIGHT,
    fontSize: 18,
  },

  coverPageHeaderContent: {
    flexDirection: 'row' as ValidFlexDirection,
    alignItems: 'center' as ValidAlignItems,
    gap: 8,
  },

  coverPageSectionContainer: {
    marginBottom: 16,
  },

  coverPageSectionTitle: {
    flexDirection: 'row' as ValidFlexDirection,
    justifyContent: 'flex-start',
    alignItems: 'center' as ValidAlignItems,
    marginBottom: 8,
  },

  coverPageDash: {
    height: 2,
    marginRight: 16,
    marginTop: 5,
    marginBottom: 4,
    width: 32,
    backgroundColor: PDF_COLORS.TEXT_MUTED,
  },

  coverPageShortDash: {
    height: 2,
    marginRight: 32,
    marginTop: 5,
    marginBottom: 4,
    width: 16,
    backgroundColor: PDF_COLORS.TEXT_MUTED,
  },

  coverPageTitleText: {
    fontSize: 12,
    lineHeight: 1.2,
    fontWeight: 'medium' as ValidFontWeight,
    color: PDF_COLORS.TEXT_MUTED,
  },

  coverPageBoldTitleText: {
    fontSize: 12,
    lineHeight: 1.2,
    fontWeight: 'bold' as ValidFontWeight,
    color: PDF_COLORS.TEXT_MUTED,
  },

  coverPageValueText: {
    fontSize: 16,
    marginBottom: 16,
  },

  coverPageValueTextLarge: {
    fontSize: 18,
    marginBottom: 8,
  },

  coverPageSectionContent: {
    marginLeft: 48,
  },

  // ================ Table of Contents Styles ================
  tocTableHeader: {
    flexDirection: 'row' as ValidFlexDirection,
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.SMALL,
    fontWeight: 'bold' as ValidFontWeight,
    marginBottom: PDF_LAYOUT.SPACING.XXLARGE,
    paddingVertical: PDF_LAYOUT.SPACING.MEDIUM,
    paddingHorizontal: PDF_LAYOUT.SPACING.LARGE,
    backgroundColor: PDF_COLORS.BACKGROUND_LIGHT_GRAY,
    borderBottomWidth: PDF_LAYOUT.BORDER_WIDTH.NORMAL,
    borderBottomColor: PDF_COLORS.BORDER_MEDIUM,
    borderBottomStyle: 'solid',
    color: PDF_COLORS.TEXT_SECONDARY,
    letterSpacing: 0.5,
  },

  tocSectionColumn: {
    width: 56,
  },

  tocTitleColumn: {
    flex: 1,
  },

  tocPageColumn: {
    width: 48,
    textAlign: 'right' as ValidTextAlign,
  },

  tocSectionRow: {
    flexDirection: 'row' as ValidFlexDirection,
    marginBottom: PDF_LAYOUT.SPACING.XLARGE,
    paddingHorizontal: PDF_LAYOUT.SPACING.LARGE,
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.MEDIUM,
    alignItems: 'baseline' as ValidAlignItems,
    lineHeight: PDF_TYPOGRAPHY.LINE_HEIGHT.NORMAL,
  },

  tocSectionNumber: {
    width: 56,
    fontWeight: 'medium' as ValidFontWeight,
    color: PDF_COLORS.TEXT_TERTIARY,
  },

  tocSectionTitle: {
    fontWeight: 'normal' as ValidFontWeight,
    color: PDF_COLORS.TEXT_PRIMARY,
    marginRight: PDF_LAYOUT.SPACING.MEDIUM,
  },

  tocDotContainer: {
    flex: 1,
    height: 12,
    alignItems: 'center' as ValidAlignItems,
    justifyContent: 'center' as ValidJustifyContent,
  },

  tocDots: {
    width: '100%',
    height: 0,
    marginTop: 2,
    borderBottom: `1 dotted ${PDF_COLORS.BORDER_MEDIUM}`,
  },

  tocSectionPage: {
    width: 48,
    textAlign: 'right' as ValidTextAlign,
    fontWeight: 'medium' as ValidFontWeight,
    color: PDF_COLORS.TEXT_SECONDARY,
    marginLeft: PDF_LAYOUT.SPACING.MEDIUM,
  },

  tocSectionTitleLink: {
    textDecoration: 'none',
    color: PDF_COLORS.TEXT_PRIMARY,
  },

  tocSectionPageLink: {
    textDecoration: 'none',
    color: PDF_COLORS.TEXT_SECONDARY,
    width: 48,
    textAlign: 'right' as ValidTextAlign,
  },
  // ================ Layout Helpers ================
  columns: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },

  columnLeft: {
    width: '48%',
  },

  columnRight: {
    width: '48%',
  },

  row: {
    flexDirection: 'row',
    marginBottom: PDF_LAYOUT.SPACING.XSMALL,
    flexWrap: 'wrap',
  },

  compactRow: {
    flexDirection: 'row',
    marginBottom: 1,
    flexWrap: 'wrap',
  },

  compactSubsection: {
    marginTop: 2,
    marginBottom: 2,
  },

  label: {
    width: '40%',
    fontWeight: 'bold',
    color: PDF_COLORS.TEXT_SECONDARY,
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.SMALL,
  },

  value: {
    width: '60%',
    color: PDF_COLORS.TEXT_PRIMARY,
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.SMALL,
  },

  // ================ Typography ================
  text: {
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.MEDIUM,
    marginBottom: 5,
    lineHeight: PDF_TYPOGRAPHY.LINE_HEIGHT.RELAXED,
  },

  textSmall: {
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.SMALL,
    marginBottom: 5,
  },

  textLarge: {
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.LARGE,
    marginBottom: 5,
    lineHeight: PDF_TYPOGRAPHY.LINE_HEIGHT.RELAXED,
  },

  // ================ Image Styles ================
  imageContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: PDF_LAYOUT.SPACING.LARGE,
    height: PDF_LAYOUT.DIMENSIONS.IMAGE_HEIGHT,
    maxHeight: PDF_LAYOUT.DIMENSIONS.MAX_IMAGE_HEIGHT,
  },

  shipHeatmapImageContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: PDF_LAYOUT.SPACING.LARGE,
    height: '100vh',
    maxHeight: '800px',
    width: '100%',
  },

  graphPlaceholder: {
    width: '100%',
    height: 200,
    backgroundColor: PDF_COLORS.BACKGROUND_LIGHT_GRAY,
    padding: 10,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },

  image: {
    maxWidth: '85%',
    maxHeight: '100%',
    objectFit: 'contain',
  },

  shipHeatmapImage: {
    width: '300%', // made much larger because we want the screenshot in the pdf to be much larger than the saved dimensions
    height: 'auto',
  },

  imageCaption: {
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.SMALL,
    fontStyle: 'italic',
    marginTop: PDF_LAYOUT.SPACING.LARGE,
    textAlign: 'left',
    paddingLeft: 60,
    paddingRight: 60,
    maxHeight: '60px',
    overflow: 'hidden',
    marginBottom: 15,
  },

  imageDescription: {
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.SMALL,
    color: PDF_COLORS.ACCENT,
    textAlign: 'left',
    marginBottom: 5,
    marginTop: 5,
    fontStyle: 'italic',
  },

  captionContainer: {
    marginTop: PDF_LAYOUT.SPACING.LARGE,
    marginBottom: PDF_LAYOUT.SPACING.XLARGE,
    flexShrink: 0,
  },

  noImageMessage: {
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.MEDIUM,
    textAlign: 'center',
    marginTop: 100,
    fontStyle: 'italic',
    color: PDF_COLORS.TEXT_TERTIARY,
  },

  // ================ Stats Container Styles ================
  statsContainer: {
    width: '30%',
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.SMALL,
    padding: PDF_LAYOUT.PADDING.CONTAINER,
    marginLeft: PDF_LAYOUT.SPACING.XXLARGE,
    backgroundColor: PDF_COLORS.BACKGROUND_OFF_WHITE,
    borderRadius: PDF_LAYOUT.BORDER_RADIUS.SMALL,
    alignSelf: 'center',
  },

  statRow: {
    marginBottom: 6,
    flexDirection: 'row',
  },

  statLabel: {
    fontWeight: 'bold' as ValidFontWeight,
    marginRight: 5,
    width: '40%',
  },

  statValue: {
    width: '60%',
  },

  sectionLabel: {
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.SMALL,
    fontWeight: 'bold' as ValidFontWeight,
    marginTop: PDF_LAYOUT.SPACING.LARGE,
    marginBottom: PDF_LAYOUT.SPACING.MEDIUM,
    borderBottomWidth: PDF_LAYOUT.BORDER_WIDTH.THIN,
    borderBottomColor: PDF_COLORS.BORDER_LIGHT,
    paddingBottom: 3,
  },

  // ================ Grid Styles ================
  // Motor Grid
  motorGridContainer: {
    marginTop: 3,
    marginBottom: 3,
  },

  motorGridHeader: {
    flexDirection: 'row',
    borderBottomWidth: PDF_LAYOUT.BORDER_WIDTH.NORMAL,
    borderBottomColor: PDF_COLORS.BORDER_MEDIUM,
    paddingBottom: 3,
  },

  motorGridHeaderCell: {
    fontWeight: 'bold' as ValidFontWeight,
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.XXSMALL,
  },

  motorGridFirstCol: {
    width: '25%',
  },

  motorGridCol: {
    width: '37.5%',
    textAlign: 'center',
  },

  motorGridRow: {
    flexDirection: 'row',
    borderBottomWidth: PDF_LAYOUT.BORDER_WIDTH.THIN,
    borderBottomColor: PDF_COLORS.BORDER_LIGHT,
    paddingVertical: 2,
  },

  motorGridLabelCell: {
    width: '25%',
    fontWeight: 'bold',
    color: PDF_COLORS.TEXT_SECONDARY,
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.XXSMALL,
  },

  motorGridValueCell: {
    width: '37.5%',
    textAlign: 'center',
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.XXSMALL,
  },

  // Calibration Grid
  calibrationGridContainer: {
    marginTop: 3,
    marginBottom: 3,
    width: '100%',
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.XXSMALL,
  },

  calibrationGridTitle: {
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.SMALL,
    fontWeight: 'bold',
    marginBottom: 3,
    color: PDF_COLORS.ACCENT,
    textDecoration: 'underline',
  },
  calibrationGridHeader: {
    flexDirection: 'row',
    borderBottomWidth: PDF_LAYOUT.BORDER_WIDTH.NORMAL,
    borderBottomColor: PDF_COLORS.BORDER_MEDIUM,
    paddingBottom: 3,
  },

  calibrationGridHeaderCell: {
    fontWeight: 'bold',
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.XXSMALL,
    textAlign: 'center',
  },

  calibrationGridFirstCol: {
    width: '40%',
  },

  calibrationGridCol: {
    width: '30%',
    textAlign: 'center',
  },

  calibrationGridRow: {
    flexDirection: 'row',
    borderBottomWidth: PDF_LAYOUT.BORDER_WIDTH.THIN,
    borderBottomColor: PDF_COLORS.BORDER_LIGHT,
    paddingVertical: 2,
  },

  calibrationGridLabelCell: {
    width: '40%',
    fontWeight: 'bold',
    color: PDF_COLORS.TEXT_SECONDARY,
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.XXSMALL,
  },

  calibrationGridValueCell: {
    width: '30%',
    textAlign: 'center',
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.XXSMALL,
  },

  // ================ Signature Styles ================
  signatureSection: {
    marginTop: PDF_LAYOUT.SPACING.XXLARGE,
  },

  contributorRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    color: PDF_COLORS.TEXT_PRIMARY,
    marginBottom: 5,
    width: '100%',
  },

  contributorHeaderText: {
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.MEDIUM,
    width: '23%',
    fontWeight: 'bold',
  },

  contributorBox: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: PDF_COLORS.BACKGROUND_LIGHT_GRAY,
    minHeight: 30,
    alignItems: 'center',
    borderRadius: PDF_LAYOUT.BORDER_RADIUS.SMALL,
    marginBottom: PDF_LAYOUT.SPACING.XLARGE,
    padding: PDF_LAYOUT.SPACING.MEDIUM,
    width: '100%',
  },

  contributorText: {
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.MEDIUM,
    width: '23%',
  },

  signatureImage: {
    width: '23%',
    height: 40,
    objectFit: 'contain',
    backgroundColor: 'transparent',
    padding: PDF_LAYOUT.SPACING.XSMALL,
    borderRadius: PDF_LAYOUT.BORDER_RADIUS.SMALL,
  },

  // ================ Content Layout for Indications ================
  contentLayout: {
    flexDirection: 'row',
    height: '65vh',
    maxHeight: '480px',
    marginTop: PDF_LAYOUT.SPACING.LARGE,
  },

  contentContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    marginBottom: PDF_LAYOUT.SPACING.XXLARGE,
  },

  // ================ Indication Summary Styles ================
  tableContainer: {
    width: '100%',
    marginTop: 16,
    marginBottom: 16,
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.SMALL,
  },

  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f0f0f0',
    borderWidth: 1,
    borderColor: '#ddd',
    borderStyle: 'solid',
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    padding: 8,
    width: '100%',
  },

  tableHeaderCell30: {
    width: '30%',
    fontWeight: 'bold',
    paddingRight: 8,
    fontSize: 8,
  },

  tableHeaderCell25: {
    width: '25%',
    fontWeight: 'bold',
    paddingRight: 8,
    fontSize: 8,
  },

  tableHeaderCell20: {
    width: '20%',
    fontWeight: 'bold',
    paddingRight: 8,
    fontSize: 8,
  },

  tableHeaderCell15: {
    width: '15%',
    fontWeight: 'bold',
    paddingRight: 8,
    fontSize: 8,
  },

  tableRowEven: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: '#ddd',
    borderStyle: 'solid',
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
    padding: 8,
    width: '100%',
  },

  tableRowOdd: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: '#ddd',
    borderStyle: 'solid',
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
    padding: 8,
    backgroundColor: '#f9f9f9',
    width: '100%',
  },

  tableCell30: {
    width: '30%',
    paddingRight: 8,
    fontSize: 8,
  },

  tableCell25: {
    width: '25%',
    paddingRight: 8,
    fontSize: 8,
  },

  tableCell20: {
    width: '20%',
    paddingRight: 8,
    fontSize: 8,
  },

  tableCell15: {
    width: '15%',
    paddingRight: 8,
    fontSize: 8,
  },

  link: {
    color: '#1890ff',
    textDecoration: 'underline',
  },

  summaryContainer: {
    marginTop: 16,
    marginBottom: 16,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#ddd',
    borderTopStyle: 'solid',
  },
})
